import React, {useEffect} from 'react';
import {Routes, Route, Navigate} from 'react-router-dom';
import {useLogoutFunction, useAuthInfo} from '@propelauth/react';
import HomePage from './pages/HomePage';
import ReportsPage from './pages/Statements/ReportsPage';
import Workbook from './pages/Statements/Workbook/Workbook';
import TransactionsPage from './pages/Statements/Workbook/TransactionPage';
import UnpaidFixedAssetsPage from './pages/Statements/Workbook/UnpaidFixedAssetsPage';
import IndirectCashflowPage from './pages/Statements/Workbook/IndirectCashflowPage';
import DisclosureGuidePage from './pages/DisclosureGuidePage';
import DisclosureDetail from './components/DisclosureDetail';
import DocumentsPage from './pages/DocumentsPage';
import CollaborativeDocument from './pages/report-workspace/V2/Document';
import PdfReviewPage from './pages/pdf-review/PdfReviewPage';
import FSLIPage from './pages/FSLIPage';
import {ThemeProvider, createTheme} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {useDispatch, useSelector} from 'react-redux';
import {
  setOrg,
  setOrgConfig,
  reset as orgReset,
  setOrgs,
  setAccessToken,
  selectEffectiveOrg,
  setHasParentOrg,
  setCurrentUser,
  setImageCookieAuthReady,
} from './data/session';
import _ from 'lodash';
import {apiSlice} from './data/api';
import {documentWorkspaceApiSlice} from './data/documentWorkspaceApi';
import DefaultLayout from './layouts/DefaultLayout';
import WorkspaceLayout from './layouts/WorkspaceLayout';
import SettingsLayout from './layouts/SettingsLayout';
import {useLDClient, useFlags} from 'launchdarkly-react-client-sdk';
import {useLogger} from './components/LoggingWrapper';

// Settings pages
import UserDetails from './pages/settings/UserDetails';
import CompanySettingsPage from './pages/settings/CompanySettingsPage';
import OrganizationUsersPage from './pages/settings/OrganizationUsersPage';
import OrganizationClientsPage from './pages/settings/OrganizationClientsPage';
import PresentationItemsPage from './pages/PresentationItemsPage';
import Reporting from './pages/settings/statements/Reporting';
import Accounts from './pages/settings/statements/Accounts';
import Departments from './pages/settings/statements/Departments';

import ParentOrgPage from './components/ParentOrgPage';
import ExternalUserLandingPage from './pages/ExternalUserLandingPage/ExternalUserLandingPage';
import ExternalUserHomePage from './pages/ExternalUserLandingPage/ExternalUserHomePage';
import ExternalUserDocumentsForOrg from './pages/ExternalUserLandingPage/ExternalUserDocumentsForOrg';
import FoldersPage from './pages/FoldersPage';
import {InternalRoute, ExternalRoute} from './components/common/RouteGuard';
import RollForwardWalkThroughPage from './pages/RollForwardWalkThroughPage';
import * as _theme from './theme';
import {
  identify,
  trackEvent,
  trackProductEvent,
  registerServiceWorker,
} from './util';
import {
  hasLoginBeenTracked,
  markLoginAsTracked,
  getBaseUserProperties,
} from './util/tracking';
import axios from 'axios';
import IdleTimerGate from './components/IdleTimerGate';
import {
  NewReleaseToast,
  useNewReleaseToast,
} from './components/common/NewReleaseToast';
import Loading from './components/Loading';

import {Amplify} from 'aws-amplify';

/** Max wait for `getImageCookies()` before opening the image gate anyway (`App.jsx`). */
const IMAGE_COOKIE_GATE_TIMEOUT_MS = 10 * 1000;

export const BLACK_100 = _theme.BLACK_100;
export const BLACK_90 = _theme.BLACK_90;
export const WHITE_100 = _theme.WHITE_100;
export const GREY_100 = _theme.GREY_100;
export const GREY_90 = _theme.GREY_90;
export const GREY_80 = _theme.GREY_80;
export const GREY_70 = _theme.GREY_70;
export const GREEN_BUTTON = _theme.GREEN_BUTTON;
export const GREY_BORDER = _theme.GREY_BORDER;
export const GREEN_NAV = _theme.GREEN_NAV;
export const GREEN_TERTIARY = _theme.GREEN_TERTIARY;
export const GREEN_100 = _theme.GREEN_100;
export const GREEN_95 = _theme.GREEN_95;
export const GREEN_80 = _theme.GREEN_80;
export const GREEN_70 = _theme.GREEN_70;
export const GREEN_60 = _theme.GREEN_60;
export const GREEN_50 = _theme.GREEN_50;

export const APP_HEADER_HEIGHT = _theme.APP_HEADER_HEIGHT;
export const FOOTER_HEIGHT = _theme.FOOTER_HEIGHT;
export const COPILOT_DRAWER_WIDTH = _theme.COPILOT_DRAWER_WIDTH;
export const COMMENT_DRAWER_WIDTH = '21.75rem';
export const HISTORY_DRAWER_WIDTH = '21.75rem';
export const DRAWER_AND_COMPOSER_WIDTH = '28.75rem';
export const MAX_PAGE_CONTENT_WIDTH = '1268px';
export const DOCUMENT_TOOLBAR_HEIGHT = _theme.DOCUMENT_TOOLBAR_HEIGHT;
export const LEFT_NAVIGATION_WIDTH = _theme.LEFT_NAVIGATION_WIDTH;

Amplify.configure({
  API: {
    Events: {
      endpoint: process.env.REACT_APP_AMPLIFY_EVENTS_ENDPOINT,
      region: 'us-west-2',
      defaultAuthMode: 'apiKey',
      apiKey: process.env.REACT_APP_AMPLIFY_EVENTS_API_KEY,
    },
  },
});

// Function to create dynamic theme based on brand color
const createDynamicTheme = (brandColor) => {
  const theme = createTheme();
  return createTheme(theme, {
    palette: {
      mode: 'light',
      background: {
        default: '#F8F8F5',
      },
      darkGreen: {
        main: GREEN_100,
        contrastText: WHITE_100,
      },
      green: {
        main: GREEN_BUTTON,
        contrastText: WHITE_100,
      },
      colors: {
        green70: GREEN_70,
        grey90: GREY_90,
      },
      black: theme.palette.augmentColor({
        color: {
          main: BLACK_100,
        },
        name: 'black',
      }),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--brand-color': brandColor,
          },
          // Always show the scrollbar on Webkit (Chrome, Safari, incl. Mac)
          '.persist-scrollbar::-webkit-scrollbar': {
            WebkitAppearance: 'none',
            width: '0.5rem',
            backgroundColor: WHITE_100,
            display: 'block !important',
          },
          '.persist-scrollbar::-webkit-scrollbar-thumb': {
            backgroundColor: GREY_90,
            borderRadius: '0.5rem',
          },
          '.persist-scrollbar::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '.persist-scrollbar': {
            scrollbarGutter: 'stable both-edges',
            msOverflowStyle: 'auto', // IE/Edge
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#26A993',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: '#26A993',
            },
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            '&.report-table': {
              '*': {
                fontSize: '12px !important',
                lineHeight: '130% !important',
              },
              '.header-row-cell': {
                backgroundColor: WHITE_100,
                borderBottom: `1px solid ${GREY_BORDER}`,
              },
              '.MuiTableCell-root': {
                padding: 0,
                '.MuiBox-root': {
                  padding: '2px 24px 2px calc(0.5rem + 20px)',
                  minWidth: '128px',
                },
              },
              '.MuiTableRow-root': {
                height: '20px',
                borderTop: '1px solid transparent',
                borderBottom: '1px solid transparent',
              },
              '.MuiTableRow-root:nth-of-type(even)': {
                backgroundColor: brandColor,
                '&:hover': {
                  backgroundColor: brandColor,
                },
              },
              '.MuiTableRow-root:nth-of-type(odd)': {
                backgroundColor: WHITE_100,
                '&:hover': {
                  backgroundColor: WHITE_100,
                },
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          colorPrimary: {
            '&.Mui-checked': {
              // Controls checked color for the thumb
              color: GREEN_100,
            },
          },
          track: {
            // Controls default (unchecked) color for the track
            opacity: 0.7,
            backgroundColor: GREEN_100,
            '.Mui-checked.Mui-checked + &': {
              // Controls checked color for the track
              opacity: 0.7,
              backgroundColor: GREEN_100,
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: BLACK_100,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: GREEN_70,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            height: '2.25rem',
            '&:nth-of-type(even)': {
              backgroundColor: '#EAEAEA',
            },
            '&:nth-of-type(odd)': {
              backgroundColor: WHITE_100,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: BLACK_100,
            fontSize: '0.875rem',
            fontWeight: '300',
            fontStyle: 'normal',
            lineHeight: '130%',
            letterSpacing: '0.0175rem',
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: GREEN_100,
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        defaultProps: {
          disableRipple: true,
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
          outlined: {
            borderColor: GREY_80,
            color: GREEN_100,
            '&:hover': {
              borderColor: GREEN_100,
              backgroundColor: GREEN_70,
            },
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          firstButton: {
            borderColor: GREEN_95,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            height: '2.25rem',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: WHITE_100,
            color: BLACK_100,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: ['Helvetica', 'Arial', 'sans-serif'].join(','),
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#27353A',
            color: '#FFF',
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: GREEN_95,
            },
          },
        },
      },
      MuiChip: {
        variants: [
          {
            props: {variant: 'open'},
            style: {
              backgroundColor: '#F5CF7F',
            },
          },
          {
            props: {variant: 'pending'},
            style: {
              backgroundColor: GREY_80,
            },
          },
          {
            props: {variant: 'closed'},
            style: {
              backgroundColor: '#50CDEB',
            },
          },
        ],
      },
    },
    typography: {
      color: BLACK_100,
      body1: {
        fontSize: '0.875rem',
        fontStyle: 'normal',
        fontWeight: '300',
        lineHeight: '130%',
        letterSpacing: '0.0175rem',
      },
      body2: {
        fontSize: '0.875rem',
        fontStyle: 'normal',
        lineHeight: '130%',
        fontWeight: '500',
        letterSpacing: '0.0175rem',
      },
      h1: {
        fontSize: '2rem',
        fontWeight: '700',
        lineHeight: '130%',
        letterSpacing: '0.04rem',
        fontStyle: 'normal',
      },
      h2: {
        fontSize: '1.5rem',
        fontWeight: '600',
        lineHeight: '130%',
        letterSpacing: '0.0325rem',
        fontStyle: 'normal',
      },
      h3: {
        fontSize: '1rem',
        fontWeight: '600',
        lineHeight: '130%',
        letterSpacing: '0.02rem',
        fontStyle: 'normal',
      },
      bodySmall: {
        fontSize: '0.75rem',
        fontStyle: 'normal',
        fontWeight: '500',
        lineHeight: '140%',
        letterSpacing: '-0.0075rem',
      },
      fixed: {
        fontSize: '0.875rem',
        fontStyle: 'normal',
        fontFamily: ['monospace'],
      },
    },
    // Custom button styles for settings and future components
    button: {
      primary: {
        large: {
          display: 'inline-flex',
          height: '40px',
          padding: '6px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          background: '#34A290',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            background: '#0F4B47',
          },
          '&:active': {
            background: '#34A290',
          },
          '&:disabled': {
            background: '#34A290',
            opacity: 0.6,
            cursor: 'not-allowed',
            color: '#fff',
          },
        },
        medium: {
          display: 'inline-flex',
          height: '36px',
          padding: '6px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          background: '#34A290',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            background: '#0F4B47',
          },
          '&:active': {
            background: '#34A290',
          },
          '&:disabled': {
            background: '#34A290',
            opacity: 0.6,
            cursor: 'not-allowed',
            color: '#fff',
          },
        },
        small: {
          display: 'inline-flex',
          height: '32px',
          padding: '6px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          background: '#34A290',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            background: '#0F4B47',
          },
          '&:active': {
            background: '#34A290',
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            color: '#fff',
          },
        },
      },
      outline: {
        large: {
          display: 'inline-flex',
          height: '40px',
          padding: '6px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          border: '1px solid #E1E5E4',
          background: 'transparent',
          color: '#162523',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            border: '1px solid #162523',
          },
          '&:active': {
            border: '1px solid #E1E5E4',
            background: '#F2F4F4',
          },
          '&:disabled': {
            border: '1px solid #E1E5E4',
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        },
        medium: {
          display: 'inline-flex',
          height: '36px',
          padding: '6px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          border: '1px solid #E1E5E4',
          background: 'transparent',
          color: '#162523',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            border: '1px solid #162523',
          },
          '&:active': {
            border: '1px solid #E1E5E4',
            background: '#F2F4F4',
          },
          '&:disabled': {
            border: '1px solid #E1E5E4',
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        },
        small: {
          display: 'inline-flex',
          height: '32px',
          padding: '6px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          border: '1px solid #E1E5E4',
          background: 'transparent',
          color: '#162523',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            border: '1px solid #162523',
          },
          '&:active': {
            border: '1px solid #E1E5E4',
            background: '#F2F4F4',
          },
          '&:disabled': {
            border: '1px solid #E1E5E4',
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        },
      },
      secondary: {
        large: {
          display: 'inline-flex',
          height: '40px',
          padding: '6px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          background: '#F2F4F4',
          color: '#162523',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            background: '#E1E5E4',
          },
          '&:active': {
            background: '#DEF1E9',
          },
          '&:disabled': {
            opacity: 0.6,
            background: '#F2F4F4',
            cursor: 'not-allowed',
          },
        },
        medium: {
          display: 'inline-flex',
          height: '36px',
          padding: '6px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          background: '#F2F4F4',
          color: '#162523',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            background: '#E1E5E4',
          },
          '&:active': {
            background: '#DEF1E9',
          },
          '&:disabled': {
            opacity: 0.6,
            background: '#F2F4F4',
            cursor: 'not-allowed',
          },
        },
        small: {
          display: 'inline-flex',
          height: '32px',
          padding: '6px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          background: '#F2F4F4',
          color: '#162523',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            background: '#F2F4F4',
          },
          '&:active': {
            background: '#DEF1E9',
          },
          '&:disabled': {
            opacity: 0.6,
            background: '#F2F4F4',
            cursor: 'not-allowed',
          },
        },
      },
      text: {
        large: {
          display: 'inline-flex',
          height: '40px',
          padding: '8px 20px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
          borderRadius: '8px',
          color: '#162523',
          fontSize: '16px',
          fontWeight: 500,
          textTransform: 'none',
          border: 'none',
          cursor: 'pointer',
          '&:hover': {
            background: '#F2F4F4',
          },
          '&:active': {
            background: '#DEF1E9',
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        },
        medium: {
          display: 'inline-flex',
          height: '36px',
          padding: '6px 16px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          borderRadius: '8px',
          color: '#162523',
          fontSize: '15px',
          fontWeight: 500,
          textTransform: 'none',
          border: 'none',
          cursor: 'pointer',
          '&:hover': {
            background: '#F2F4F4',
          },
          '&:active': {
            background: '#DEF1E9',
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        },
        small: {
          display: 'inline-flex',
          height: '32px',
          padding: '4px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          borderRadius: '8px',
          color: '#162523',
          fontSize: '12px',
          fontWeight: 500,
          textTransform: 'none',
          border: 'none',
          cursor: 'pointer',
          '&:hover': {
            background: '#F2F4F4',
          },
          '&:active': {
            background: '#DEF1E9',
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        },
      },
    },
  });
};

const sortOrgs = (orgs) => {
  return orgs.sort((a, b) => {
    const nameA = a.orgName.toUpperCase();
    const nameB = b.orgName.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    return 0;
  });
};

// ENG-1737 - Added axios interceptor to track API requests and responses
axios.interceptors.request.use((request) => {
  request.customData = {startTime: new Date().getTime()};
  return request;
});

axios.interceptors.response.use(
  (response) => {
    trackEvent(
      'API Request Completed',
      _.pick(response.config, ['method', 'url']),
      new Date().getTime() -
        _.get(response, 'config.customData.startTime', new Date().getTime())
    );
    return response;
  },
  (error) => {
    trackEvent(
      'API Request Failed',
      {
        status: _.get(error, 'response.status', 'UNKNOWN'),
        ..._.pick(error.config, ['method', 'url']),
      },
      new Date().getTime() -
        _.get(error, 'config.customData.startTime', new Date().getTime())
    );
    console.error('API Request Failed', {
      status: _.get(error, 'response.status', 'UNKNOWN'),
      ..._.pick(error.config, ['method', 'url']),
      duration:
        new Date().getTime() -
        _.get(error, 'config.customData.startTime', new Date().getTime()),
    });
    return Promise.reject(error);
  }
);

function App() {
  const logger = useLogger();
  const authInfo = useAuthInfo();
  const {
    loading: authLoading = false,
    isLoggedIn = false,
    orgHelper,
    tokens,
    user,
    userClass,
  } = authInfo || {};
  const dispatch = useDispatch();
  const ldClient = useLDClient();
  const flags = useFlags();
  const {documentFolders: documentFoldersEnabled} = flags;
  const {
    currentOrg,
    isAuthenticated,
    isShareLinkMode,
    orgConfig,
    hasParentOrg,
    isExternalUser,
  } = useSelector((state) => state.session);
  const logout = useLogoutFunction();
  const [trigger, result, lastPromiseInfo] =
    apiSlice.endpoints.getOrg.useLazyQuery({preferCacheValue: true});
  const [getUserIntercomHash] = apiSlice.endpoints.getCurrentUser.useLazyQuery({
    preferCacheValue: true,
  });
  const [getImageCookies] =
    documentWorkspaceApiSlice.endpoints.getImageCookies.useLazyQuery();
  const {showToast, dismissToast, reloadPage} = useNewReleaseToast();
  // Create dynamic theme based on brand color
  const brandColor = orgConfig?.brandColor || GREEN_TERTIARY;
  const dynamicTheme = createDynamicTheme(brandColor);

  const effectiveOrg = useSelector(selectEffectiveOrg);

  const isOrgConfigError = isAuthenticated && result.isError;

  useEffect(() => {
    registerServiceWorker();
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const _orgs = sortOrgs(orgHelper.getOrgs());
      const hasParentOrg = _orgs.find(
        (org) => org.orgMetadata?.isParentOrganization
      );
      if (hasParentOrg) {
        dispatch(setHasParentOrg({hasParentOrg: true}));
      }
      dispatch(setOrgs({orgs: _orgs}));

      // Get previously selected org ID from localStorage
      const storedOrgId = localStorage.getItem('selectedOrgId');
      // If there is a previously selected org ID, use that, otherwise use the first org
      const initialOrg = storedOrgId
        ? _orgs.find((org) => org.orgId === storedOrgId) || _orgs[0]
        : _orgs[0];

      dispatch(setOrg({org: initialOrg}));
      identify(ldClient, user, initialOrg, logger);

      // Track login event once per session
      if (!hasLoginBeenTracked()) {
        trackProductEvent('User Logged In', {
          ...getBaseUserProperties(user, initialOrg),
          created_at: user?.createdAt || null,
        });
        markLoginAsTracked();
      }
    }
    return () => {
      dispatch(orgReset());
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (result.currentData) {
      logger.debug('Setting org config', {
        orgConfig: result,
        lastPromiseInfo,
        currentOrg,
      });
      dispatch(setOrgConfig({orgConfig: result.currentData}));
    }
  }, [result]);

  useEffect(() => {
    if (!_.isNil(userClass) && !_.isEmpty(currentOrg?.orgId)) {
      const _userOrg = userClass.getOrg(currentOrg.orgId);
      if (_.isNil(_userOrg)) {
        logout(false);
      }
    }
  }, [userClass, currentOrg]);

  useEffect(() => {
    identify(ldClient, user, currentOrg, logger);
    dispatch(setCurrentUser({currentUser: user}));
    const _setAccessToken = async () => {
      if (_.isNil(tokens)) return;

      // If there is a current org, use that (we would get the access token that is associated with the org)
      if (!_.isEmpty(currentOrg?.orgId)) {
        tokens
          .getAccessTokenForOrg(currentOrg.orgId)
          .then(({accessToken, error}) => {
            if (error) {
              console.error('Error loading access token', {error});
            } else {
              dispatch(setAccessToken({accessToken}));
            }
          })
          .catch((_error) => {
            console.error('Error loading access token', {error: _error});
          });
      } else if (
        // If the user is in a shared document mode, use the access token that is for the user (there will be no org-specific token)
        isExternalUser ||
        location.search.includes('shared=true') ||
        isShareLinkMode
      ) {
        const accessToken = await tokens.getAccessToken();
        dispatch(setAccessToken({accessToken}));
      }
    };

    _setAccessToken();
    const _intervalId = setInterval(_setAccessToken, 25 * 60 * 1000); // Refresh the access token every 25 minutes

    return () => clearInterval(_intervalId);
  }, [user, currentOrg, isExternalUser, isShareLinkMode]);

  useEffect(() => {
    if (_.isEmpty(effectiveOrg?.orgId) || !isAuthenticated) {
      return;
    }

    dispatch(setImageCookieAuthReady({ready: false}));

    dispatch(apiSlice.util.resetApiState());

    trigger(effectiveOrg.orgId);

    // Fetch CloudFront signed cookies/params for viewing images.
    // If signed params are present and a service worker controls the page,
    // pass them through for SW-based image requests.
    let cancelled = false;

    const fetchImageCookiesPromise = getImageCookies().then((result) => {
      if (result.data?.signedParams && navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SET_CLOUDFRONT_PARAMS',
          params: result.data.signedParams,
          expiry: result.data.expiry || Date.now() + 23 * 60 * 60 * 1000, // 23 hours default
        });
      }
    });

    Promise.race([
      fetchImageCookiesPromise,
      new Promise((resolve) => {
        setTimeout(resolve, IMAGE_COOKIE_GATE_TIMEOUT_MS);
      }),
    ])
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          dispatch(setImageCookieAuthReady({ready: true}));
        }
      });

    getUserIntercomHash()
      .then((result) => {
        const userIntercomHash = result.data?.userIntercomHash;
        identify(ldClient, user, effectiveOrg, logger, userIntercomHash);
      })
      .catch((error) => {
        logger.error('Failed to fetch userIntercomHash', error);
        // Still identify user if can't fetch intercom hash
        identify(ldClient, user, effectiveOrg, logger);
      });

    return () => {
      cancelled = true;
    };
  }, [effectiveOrg, isAuthenticated]);

  if (authLoading || !isLoggedIn) {
    return null;
  }

  return (
    <IdleTimerGate onIdle={() => logout(true)}>
      <ThemeProvider theme={dynamicTheme}>
        <CssBaseline />
        <Routes>
          <Route path={'/'} element={<DefaultLayout />}>
            <Route
              index
              element={(() => {
                if (!isAuthenticated || isOrgConfigError) {
                  return <HomePage />;
                }

                if (result.isLoading && hasParentOrg) {
                  return <Loading message="Loading clients..." />;
                }

                if (isAuthenticated && isExternalUser) {
                  return <Navigate replace to="/shared" />;
                }

                if (isAuthenticated && hasParentOrg) {
                  return <ParentOrgPage userFirstName={user.firstName} />;
                } else {
                  return <HomePage />;
                }
              })()}
            />
            {/* Internal user routes - redirects external users to /shared */}
            <Route element={<InternalRoute />}>
              <Route path={'statements'} element={<ReportsPage />} />
              <Route
                path="statements/indirect-cashflow-workbook"
                element={
                  isAuthenticated && flags.indirectCashFlow === false ? (
                    <Navigate replace to="/statements" />
                  ) : (
                    <Workbook />
                  )
                }
              >
                <Route index element={<IndirectCashflowPage />} />
                <Route path=":id/transactions" element={<TransactionsPage />} />
                <Route
                  path=":id/unpaid-fixed-assets"
                  element={<UnpaidFixedAssetsPage />}
                />
              </Route>
              <Route path={'documents'} element={<DocumentsPage />} />
              <Route
                path={
                  documentFoldersEnabled
                    ? 'folders/:folderId/documents'
                    : 'documents'
                }
                element={<DocumentsPage />}
              />
              <Route
                path={'fslis'}
                element={
                  _.includes(
                    ['off', 'shadow'],
                    flags.accountConfigurationMigration
                  ) ? (
                    <FSLIPage />
                  ) : (
                    <PresentationItemsPage />
                  )
                }
              />
              <Route
                path={'presentationitems'}
                element={<PresentationItemsPage />}
              />
              <Route
                path={documentFoldersEnabled ? 'folders' : 'documents'}
                element={<FoldersPage />}
              />
              {/* Parent org requires hasParentOrg (external users won't have this) */}
              <Route
                path={'parent-organization'}
                element={
                  isAuthenticated && hasParentOrg ? (
                    <ParentOrgPage userFirstName={user.firstName} />
                  ) : (
                    <Navigate replace to="/" />
                  )
                }
              />
            </Route>

            {/* Public routes - accessible to all authenticated users */}
            <Route
              path={'disclosure-library'}
              element={<DisclosureGuidePage />}
            />
            <Route
              path={'disclosure-library/:slug'}
              element={<DisclosureDetail />}
            />

            {/* External user routes - redirects internal users to / */}
            <Route element={<ExternalRoute />}>
              <Route path={'shared'} element={<ExternalUserLandingPage />}>
                <Route index element={<ExternalUserHomePage />} />
                <Route
                  path={'org/:orgId'}
                  element={<ExternalUserDocumentsForOrg />}
                />
              </Route>
            </Route>
          </Route>

          <Route path={'/'} element={<WorkspaceLayout />}>
            <Route
              path={'documents/:documentId'}
              element={<CollaborativeDocument />}
            />
            <Route
              path={'documents/pdf-review/:documentId'}
              element={<PdfReviewPage />}
            />

            {/* Internal only */}
            <Route element={<InternalRoute />}>
              <Route
                path={'rollforward-walk-through/:documentId'}
                element={<RollForwardWalkThroughPage />}
              />
            </Route>
          </Route>

          {/* Internal/External user settings routes */}
          <Route path={'/settings'} element={<SettingsLayout />}>
            <Route
              index
              element={<Navigate replace to="/settings/my-account" />}
            />
            <Route path={'my-account'} element={<UserDetails />} />
            <Route
              path={'organization/users'}
              element={<OrganizationUsersPage />}
            />
            <Route
              path={'organization/clients'}
              element={<OrganizationClientsPage />}
            />
            <Route
              path={':companySlug/:tab'}
              element={<CompanySettingsPage />}
            />
            <Route path={'reporting'} element={<Reporting />} />
            <Route path={'reporting/accounts'} element={<Accounts />} />
            <Route path={'reporting/departments'} element={<Departments />} />
          </Route>

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <NewReleaseToast
          show={showToast}
          reloadPage={reloadPage}
          dismissToast={dismissToast}
        />
      </ThemeProvider>
    </IdleTimerGate>
  );
}

export default App;
